import React, { useEffect, useRef } from 'react';
import { TERRITOIRES } from './FTPEOutremerData';

const TerritoiresWatermark = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const nodesRef = useRef([]);
  const scrollRef = useRef(0);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    const updateCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight; // Viewport visible seulement
      
      // Distribuer les 12 territoires sur l'écran visible en grille 4x3
      const cols = 4;
      const rows = 3;
      const cellWidth = canvas.width / cols;
      const cellHeight = canvas.height / rows;
      
      nodesRef.current = TERRITOIRES.map((territoire, index) => {
        const col = index % cols;
        const row = Math.floor(index / cols);
        
        return {
          ...territoire,
          x: (col + 0.5) * cellWidth + (Math.random() - 0.5) * 100,
          y: (row + 0.5) * cellHeight + (Math.random() - 0.5) * 60,
          vx: (Math.random() - 0.5) * 1.2, // Mouvement plus rapide
          vy: (Math.random() - 0.5) * 1.0,
          radius: 6 + Math.random() * 2,
          pulsePhase: Math.random() * Math.PI * 2,
          arcOffset: Math.random() * Math.PI * 2,
        };
      });
    };
    
    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    
    // Écouter le scroll
    const handleScroll = () => {
      scrollRef.current = window.scrollY;
    };
    window.addEventListener('scroll', handleScroll);
    
    // Vérifier si la page a changé de taille
    let lastScrollHeight = document.documentElement.scrollHeight;
    const checkScrollHeight = setInterval(() => {
      const newScrollHeight = document.documentElement.scrollHeight;
      if (newScrollHeight !== lastScrollHeight) {
        lastScrollHeight = newScrollHeight;
        updateCanvasSize();
      }
    }, 1000);
    
    // Fonction pour dessiner une flèche
    const _drawArrow = (ctx, fromX, fromY, toX, toY, color, size = 8) => {
      const angle = Math.atan2(toY - fromY, toX - fromX);
      ctx.beginPath();
      ctx.moveTo(toX, toY);
      ctx.lineTo(toX - size * Math.cos(angle - Math.PI / 6), toY - size * Math.sin(angle - Math.PI / 6));
      ctx.lineTo(toX - size * Math.cos(angle + Math.PI / 6), toY - size * Math.sin(angle + Math.PI / 6));
      ctx.closePath();
      ctx.fillStyle = color;
      ctx.fill();
    };
    
    // Fonction pour dessiner un arc de Bézier avec flèches (version discrète)
    const drawArcConnection = (ctx, node1, node2, time, opacity, index) => {
      const dx = node2.x - node1.x;
      const dy = node2.y - node1.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      
      // Point de contrôle pour la courbe de Bézier (arc plus subtil)
      const midX = (node1.x + node2.x) / 2;
      const midY = (node1.y + node2.y) / 2;
      const curvature = 40 + Math.sin(time + index) * 15; // Arc plus petit
      const perpX = -dy / dist * curvature;
      const perpY = dx / dist * curvature;
      const ctrlX = midX + perpX;
      const ctrlY = midY + perpY;
      
      // Dessiner l'arc principal (sans halo)
      ctx.beginPath();
      ctx.moveTo(node1.x, node1.y);
      ctx.quadraticCurveTo(ctrlX, ctrlY, node2.x, node2.y);
      ctx.strokeStyle = `rgba(52, 211, 153, ${opacity * 0.4})`;
      ctx.lineWidth = 1;
      ctx.stroke();
      
      // Une seule flèche animée par connexion (plus discrète)
      const t = (time * 0.1 + node1.arcOffset) % 1;
      
      // Calcul du point sur la courbe
      const px = (1 - t) * (1 - t) * node1.x + 2 * (1 - t) * t * ctrlX + t * t * node2.x;
      const py = (1 - t) * (1 - t) * node1.y + 2 * (1 - t) * t * ctrlY + t * t * node2.y;
      
      // Calcul de la tangente
      const tx = 2 * (1 - t) * (ctrlX - node1.x) + 2 * t * (node2.x - ctrlX);
      const ty = 2 * (1 - t) * (ctrlY - node1.y) + 2 * t * (node2.y - ctrlY);
      const angle = Math.atan2(ty, tx);
      
      // Petite flèche discrète
      ctx.save();
      ctx.translate(px, py);
      ctx.rotate(angle);
      
      ctx.beginPath();
      ctx.moveTo(4, 0);
      ctx.lineTo(-2, -2);
      ctx.lineTo(-1, 0);
      ctx.lineTo(-2, 2);
      ctx.closePath();
      ctx.fillStyle = `rgba(255, 255, 255, ${opacity * 0.3})`;
      ctx.fill();
      
      ctx.restore();
    };
    
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      const nodes = nodesRef.current;
      const time = Date.now() * 0.001;
      const scroll = scrollRef.current;
      
      // Mettre à jour les positions (mouvement dynamique)
      nodes.forEach(node => {
        node.x += node.vx;
        node.y += node.vy;
        
        // Rebondir sur les bords
        if (node.x < 100 || node.x > canvas.width - 100) node.vx *= -1;
        if (node.y < 50 || node.y > canvas.height - 50) node.vy *= -1;
        
        // Garder dans les limites visibles
        node.x = Math.max(100, Math.min(canvas.width - 100, node.x));
        node.y = Math.max(50, Math.min(canvas.height - 50, node.y));
      });
      
      // ═══════════════════════════════════════════════════════════════
      // CONNEXIONS EN ARC DISCRÈTES
      // ═══════════════════════════════════════════════════════════════
      
      // Dessiner les arcs de connexion (plus subtils)
      let connectionIndex = 0;
      nodes.forEach((node, i) => {
        nodes.forEach((other, j) => {
          if (i >= j) return;
          const dx = other.x - node.x;
          const dy = other.y - node.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          
          if (dist < 600) {
            const opacity = (1 - dist / 600) * 0.25; // Opacité réduite
            drawArcConnection(ctx, node, other, time, opacity, connectionIndex);
            connectionIndex++;
          }
        });
      });
      
      // ═══════════════════════════════════════════════════════════════
      // NŒUDS TERRITOIRES DISCRETS
      // ═══════════════════════════════════════════════════════════════
      
      nodes.forEach((node) => {
        const pulse = Math.sin(time * 1.5 + node.pulsePhase) * 0.2 + 0.8;
        const baseOpacity = 0.25; // Opacité très réduite
        
        // Petit halo subtil
        const haloGradient = ctx.createRadialGradient(
          node.x, node.y, 0,
          node.x, node.y, node.radius * 6
        );
        haloGradient.addColorStop(0, `rgba(34, 197, 94, ${baseOpacity * pulse * 0.3})`);
        haloGradient.addColorStop(1, 'rgba(34, 197, 94, 0)');
        
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius * 6, 0, Math.PI * 2);
        ctx.fillStyle = haloGradient;
        ctx.fill();
        
        // Couleur selon le type
        let glowColor;
        switch (node.type) {
          case 'DROM':
            glowColor = '34, 197, 94';
            break;
          case 'COM':
            glowColor = '16, 185, 129';
            break;
          case 'PTOM':
            glowColor = '52, 211, 153';
            break;
          default:
            glowColor = '34, 197, 94';
        }
        
        // Petit cercle
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius * 0.8, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${glowColor}, ${baseOpacity * pulse * 1.5})`;
        ctx.fill();
        
        // Anneau fin
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius * 0.8 + 2, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(255, 255, 255, ${0.1 * pulse})`;
        ctx.lineWidth = 1;
        ctx.stroke();
        
        // Texte très discret (sans fond)
        const textY = node.y + node.radius + 12;
        
        // Nom du territoire
        ctx.font = '10px Inter, system-ui, sans-serif';
        ctx.fillStyle = `rgba(255, 255, 255, ${0.15 + pulse * 0.1})`;
        ctx.textAlign = 'center';
        ctx.fillText(node.nom, node.x, textY);
        
        // Type
        ctx.font = '8px monospace';
        ctx.fillStyle = `rgba(${glowColor}, ${0.15 + pulse * 0.05})`;
        ctx.fillText(node.type, node.x, textY + 10);
      });
      
      // Particules très discrètes
      const numParticles = Math.floor(canvas.height / 60);
      for (let i = 0; i < numParticles; i++) {
        const px = (Math.sin(time * 0.15 + i * 0.6) * 0.5 + 0.5) * canvas.width;
        const py = ((i / numParticles) * canvas.height + time * 8) % canvas.height;
        const pSize = 1;
        const pOpacity = 0.04 + Math.sin(time * 2 + i) * 0.02;
        
        ctx.beginPath();
        ctx.arc(px, py, pSize, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(52, 211, 153, ${pOpacity})`;
        ctx.fill();
      }
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      window.removeEventListener('resize', updateCanvasSize);
      window.removeEventListener('scroll', handleScroll);
      clearInterval(checkScrollHeight);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);
  
  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0"
      style={{ opacity: 0.6 }}
    />
  );
};

export default TerritoiresWatermark;
